// Locale-aware parsing of amounts and dates. Bank statements across the Balkans
// and RU banks use comma decimals ("1.234,56"), spaces as thousands separators,
// and several date layouts — templates declare which, this module interprets.

export interface NumberLocale {
  decimalSeparator: "," | ".";
  /** "" means no grouping separator. */
  thousandsSeparator: "," | "." | " " | " " | "";
}

/**
 * Parse a localized number string into a JS number.
 * Handles grouping separators, comma/dot decimals, leading/trailing minus,
 * and accounting-style parentheses for negatives. Returns null if no number.
 */
export function parseAmount(
  raw: string,
  locale: NumberLocale,
): number | null {
  if (raw == null) return null;
  let s = raw.trim();
  if (!s) return null;

  let negative = false;

  // Accounting negatives: (1.234,56)
  if (/^\(.*\)$/.test(s)) {
    negative = true;
    s = s.slice(1, -1).trim();
  }
  // Trailing/leading minus, or trailing debit marker.
  if (/-\s*$/.test(s) || /^-\s*/.test(s)) {
    negative = true;
    s = s.replace(/-/g, "").trim();
  }

  // Keep only digits and separators.
  s = s.replace(/[^0-9.,  ]/g, "").trim();
  if (!s) return null;

  // Remove grouping separators, normalise decimal separator to ".".
  if (locale.thousandsSeparator) {
    s = s.split(locale.thousandsSeparator).join("");
  }
  if (locale.decimalSeparator === ",") {
    // Any remaining dots are stray grouping separators; commas are decimals.
    s = s.replace(/\./g, "").replace(",", ".");
  } else {
    // Dot decimals: strip any stray commas/spaces used as grouping.
    s = s.replace(/[,\s ]/g, "");
  }

  if (!/[0-9]/.test(s)) return null;
  const n = Number.parseFloat(s);
  if (Number.isNaN(n)) return null;
  return negative ? -n : n;
}

const MONTH_INDEX: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

/**
 * Parse a date against a list of format hints, returning ISO YYYY-MM-DD.
 * Supported tokens: DD, MM, YYYY, YY, MON (3-letter English month).
 * The first format that fully matches wins. Returns null on no match.
 */
export function parseDate(raw: string, formats: string[]): string | null {
  const s = raw.trim();
  if (!s) return null;

  for (const fmt of formats) {
    const iso = tryFormat(s, fmt);
    if (iso) return iso;
  }
  return null;
}

function tryFormat(s: string, fmt: string): string | null {
  // Build a regex from the format, capturing named groups.
  const tokenOrder: string[] = [];
  let pattern = "^";
  let i = 0;
  while (i < fmt.length) {
    const rest = fmt.slice(i);
    if (rest.startsWith("YYYY")) {
      pattern += "(\\d{4})";
      tokenOrder.push("YYYY");
      i += 4;
    } else if (rest.startsWith("YY")) {
      pattern += "(\\d{2})";
      tokenOrder.push("YY");
      i += 2;
    } else if (rest.startsWith("MON")) {
      pattern += "([A-Za-z]{3})";
      tokenOrder.push("MON");
      i += 3;
    } else if (rest.startsWith("MM")) {
      pattern += "(\\d{1,2})";
      tokenOrder.push("MM");
      i += 2;
    } else if (rest.startsWith("DD")) {
      pattern += "(\\d{1,2})";
      tokenOrder.push("DD");
      i += 2;
    } else {
      // Literal separator char (escape regex specials).
      pattern += fmt[i]!.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      i += 1;
    }
  }
  pattern += "$";

  const m = new RegExp(pattern).exec(s);
  if (!m) return null;

  let day = 1;
  let month = 1;
  let year = 1970;
  tokenOrder.forEach((tok, idx) => {
    const val = m[idx + 1]!;
    switch (tok) {
      case "YYYY":
        year = Number.parseInt(val, 10);
        break;
      case "YY": {
        const yy = Number.parseInt(val, 10);
        year = yy >= 70 ? 1900 + yy : 2000 + yy;
        break;
      }
      case "MM":
        month = Number.parseInt(val, 10);
        break;
      case "MON":
        month = MONTH_INDEX[val.toLowerCase()] ?? NaN;
        break;
      case "DD":
        day = Number.parseInt(val, 10);
        break;
    }
  });

  if (
    Number.isNaN(month) ||
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31
  ) {
    return null;
  }

  const pad = (n: number, w = 2) => String(n).padStart(w, "0");
  return `${pad(year, 4)}-${pad(month)}-${pad(day)}`;
}
